import React, { useCallback, useMemo, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { getModerationReports, resolveModerationReport } from "../../../data/admin/moderation";

const REASON_LABELS = {
    Inapproprie: "Inapproprié",
    Spam: "Spam",
    Arnaque: "Arnaque",
    Offensant: "Offensant",
    Autre: "Autre",
};

const STATUS_LABELS = {
    Pending: "En attente",
    ActionTaken: "Traité",
    Dismissed: "Rejeté",
};

const STATUS_FILTERS = [
    { value: "Pending", label: "En attente" },
    { value: "", label: "Tous" },
    { value: "ActionTaken", label: "Traités" },
    { value: "Dismissed", label: "Rejetés" },
];

export async function loader() {
    const data = await getModerationReports("Pending", 1, 100);
    return { data };
}

function formatDateTime(value) {
    if (!value) {
        return "";
    }
    return new Date(value).toLocaleString("fr-FR");
}

function targetLabel(report) {
    if (report.targetType === "Annonce") {
        const owner = report.targetToolOwnerName ? ` — ${report.targetToolOwnerName}` : "";
        return `Annonce : ${report.targetToolName || report.targetToolPublicId || report.targetToolId}${owner}`;
    }
    const email = report.targetUserEmail ? ` (${report.targetUserEmail})` : "";
    return `Loueur : ${report.targetUserName || report.targetUserId}${email}`;
}

export default function AdminModerationPage() {
    const initial = useLoaderData();
    const [status, setStatus] = useState("Pending");
    const [reports, setReports] = useState(initial?.data?.items ?? []);
    const [total, setTotal] = useState(initial?.data?.total ?? 0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const reload = useCallback(async (nextStatus) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getModerationReports(nextStatus, 1, 100);
            setReports(data?.items ?? []);
            setTotal(data?.total ?? 0);
        } catch (e) {
            setError(e.message || "Erreur de chargement.");
        } finally {
            setLoading(false);
        }
    }, []);

    const onStatusChange = useCallback((value) => {
        setStatus(value);
        reload(value);
    }, [reload]);

    const onResolve = useCallback(async (report, action) => {
        const actionLabels = {
            ListingDeactivated: "désactiver cette annonce",
            UserBanned: "bannir ce loueur (blocage du compte + désactivation de ses annonces)",
            Dismissed: "rejeter ce signalement",
        };
        if (!window.confirm(`Confirmer : ${actionLabels[action]} ?`)) {
            return;
        }
        const note = window.prompt("Note interne (optionnelle) :", "") || null;
        setLoading(true);
        setError(null);
        try {
            await resolveModerationReport(report.id, action, note);
            await reload(status);
        } catch (e) {
            setError(e.message || "Erreur lors du traitement.");
            setLoading(false);
        }
    }, [reload, status]);

    const heading = useMemo(() => `Signalements (${total})`, [total]);

    return (
        <div className="admin-page admin-moderation" style={{ padding: "16px" }}>
            <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                <h1 style={{ margin: 0 }}>Modération</h1>
                <div style={{ display: "flex", gap: 8 }}>
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.value || "all"}
                            type="button"
                            onClick={() => onStatusChange(f.value)}
                            className={status === f.value ? "btn btn--primary" : "btn"}
                            style={{
                                padding: "6px 12px",
                                borderRadius: 8,
                                border: "1px solid #d0d0d0",
                                cursor: "pointer",
                                background: status === f.value ? "#2b6cb0" : "#fff",
                                color: status === f.value ? "#fff" : "#333",
                            }}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </header>

            <p style={{ color: "#666", marginTop: 8 }}>{heading}</p>

            {error && <div style={{ color: "#c0392b", margin: "8px 0" }}>{error}</div>}
            {loading && <div style={{ color: "#666", margin: "8px 0" }}>Chargement…</div>}

            {!loading && reports.length === 0 && (
                <div style={{ color: "#666", padding: "24px 0" }}>Aucun signalement dans cette catégorie.</div>
            )}

            {reports.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
                        <thead>
                            <tr style={{ textAlign: "left", borderBottom: "2px solid #eee" }}>
                                <th style={{ padding: 8 }}>Date</th>
                                <th style={{ padding: 8 }}>Cible</th>
                                <th style={{ padding: 8 }}>Motif</th>
                                <th style={{ padding: 8 }}>Auteur</th>
                                <th style={{ padding: 8 }}>Commentaire</th>
                                <th style={{ padding: 8 }}>Statut</th>
                                <th style={{ padding: 8 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map((report) => (
                                <tr key={report.id} style={{ borderBottom: "1px solid #f0f0f0", verticalAlign: "top" }}>
                                    <td style={{ padding: 8, whiteSpace: "nowrap" }}>{formatDateTime(report.createdAtUtc)}</td>
                                    <td style={{ padding: 8 }}>{targetLabel(report)}</td>
                                    <td style={{ padding: 8 }}>{REASON_LABELS[report.reason] || report.reason}</td>
                                    <td style={{ padding: 8 }}>{report.reporterName}{report.reporterEmail ? ` (${report.reporterEmail})` : ""}</td>
                                    <td style={{ padding: 8, maxWidth: 280, whiteSpace: "pre-wrap" }}>{report.comment}</td>
                                    <td style={{ padding: 8 }}>
                                        {STATUS_LABELS[report.status] || report.status}
                                        {report.status !== "Pending" && report.reviewedBy && (
                                            <div style={{ color: "#888", fontSize: 12 }}>
                                                par {report.reviewedBy}
                                                {report.reviewedAtUtc ? ` le ${formatDateTime(report.reviewedAtUtc)}` : ""}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: 8, whiteSpace: "nowrap" }}>
                                        {report.status === "Pending" ? (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                {report.targetType === "Annonce" && report.targetToolId && (
                                                    <button type="button" onClick={() => onResolve(report, "ListingDeactivated")} disabled={loading}>
                                                        Désactiver l'annonce
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => onResolve(report, "UserBanned")} disabled={loading}>
                                                    Bannir le loueur
                                                </button>
                                                <button type="button" onClick={() => onResolve(report, "Dismissed")} disabled={loading}>
                                                    Rejeter
                                                </button>
                                            </div>
                                        ) : (
                                            <span style={{ color: "#888" }}>{report.actionTaken}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
